import { useEffect, useId, useRef, type ReactNode } from "react";
import { useFocusTrap } from "../lib/focus-trap.ts";
import { CloseIcon } from "./Icons.tsx";

/**
 * The detail panel of the desktop shell.
 *
 * At 1024px and up it slides in from the right and sits over the page, so the list the owner was
 * reading stays where it was; below that it is the whole screen, because a 390px phone has no
 * room beside anything. It replaces `Sheet` in the owner and admin areas: a sheet rises to a thumb
 * at the bottom of a phone, and on a laptop the bottom of the window is the far end of the screen.
 *
 * A `<dialog>` element shown with `open` rather than `showModal()`, so that the trap, Escape and
 * the focus return are the same code at both widths and under test (`useFocusTrap`); a modal
 * dialog would also paint over the whole viewport, and the panel is meant to sit beside the page
 * at desktop width. `aria-modal` still tells assistive tech the page behind is out of reach, and
 * the body stops scrolling while it is open.
 *
 * `placement="center"` is the same panel as a large dialog in the middle of the window at desktop
 * width, for a form with many parts that needs the room (Add a package, 2026-10-30). Below 1024px
 * it is the whole screen, the same as the side panel.
 */
export function Panel({
  open,
  onClose,
  title,
  subtitle,
  actions,
  width = "md",
  placement = "side",
  flush = false,
  header = true,
  animate = true,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  /** Pinned to the bottom of the panel. */
  actions?: ReactNode;
  /**
   * `md` is 480px, `lg` is 640px, `xl` is 960px (never more than 90% of the window), at desktop
   * width. `xl` is for a tool with wide rows, such as WhatsApp sync.
   */
  width?: "md" | "lg" | "xl";
  /** `side` slides in from the right; `center` opens in the middle of the window, at desktop width. */
  placement?: "side" | "center";
  /**
   * The body has no padding and does not scroll: the content lays itself out in the full height. A
   * chat needs it, so that its turns scroll and its composer stays at the bottom.
   */
  flush?: boolean;
  /**
   * False: no header bar. The content brings its own title and its own way out (the session rail's
   * Done), and the title stays for screen readers. Escape and the backdrop still close the panel.
   */
  header?: boolean;
  /**
   * False: the panel does not slide in. For the next step of a panel that is open already (Record a
   * payment: what the payment is for, then the form), so the step changes in place.
   */
  animate?: boolean;
  children?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useFocusTrap(ref, open, onClose);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className={
        placement === "center"
          ? "fixed inset-0 z-50 flex justify-end lg:items-center lg:justify-center lg:p-6"
          : "fixed inset-0 z-50 flex justify-end"
      }
    >
      {/* A click target only. Keyboard users close with Escape or the header button inside the trap. */}
      <button
        type="button"
        tabIndex={-1}
        aria-label={`Close ${title}`}
        onClick={onClose}
        className="absolute inset-0 bg-ink/35 backdrop-blur-[2px]"
      />
      <dialog
        ref={ref}
        open
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={[
          // Reset the element's own margin, border, padding and sizing; it is laid out by its parent.
          "relative m-0 flex h-full max-h-none w-full max-w-none flex-col border-0 bg-paper p-0 text-ink",
          "outline-none",
          placement === "center"
            ? "lg:h-auto lg:max-h-[min(880px,92vh)] lg:overflow-hidden lg:rounded-card lg:shadow-[0_24px_64px_-20px_rgba(16,25,23,0.5)]"
            : "shadow-[-8px_0_40px_-16px_rgba(16,25,23,0.45)]",
          animate ? (placement === "center" ? "motion-safe:animate-sheet" : "motion-safe:animate-panel") : "",
          { md: "lg:max-w-[480px]", lg: "lg:max-w-[640px]", xl: "lg:max-w-[min(960px,90vw)]" }[width],
        ].join(" ")}
      >
        {header ? (
          <div className="flex items-start gap-3 border-b border-line px-5 py-4">
            <div className="min-w-0 flex-1">
              <h2 id={titleId} className="text-[21px]">
                {title}
              </h2>
              {subtitle ? <p className="mt-1 text-[13px] text-ink-2">{subtitle}</p> : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={`Close ${title}`}
              className="flex size-9 shrink-0 items-center justify-center rounded-full border border-line text-ink-2 transition-colors duration-150 hover:bg-sunken active:bg-sunken"
            >
              <CloseIcon size={17} />
            </button>
          </div>
        ) : (
          <h2 id={titleId} className="sr-only">
            {title}
          </h2>
        )}

        {flush ? (
          <div className="flex min-h-0 flex-1 flex-col">{children}</div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">{children}</div>
        )}

        {actions ? (
          <div className="flex flex-col gap-2 border-t border-line px-5 pt-4 pb-5 sm:flex-row-reverse sm:justify-start">
            {actions}
          </div>
        ) : null}
      </dialog>
    </div>
  );
}
